import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './PlanCheckout.css';
import { CheckCircle, CreditCardRefresh, XClose } from '@untitledui/icons';
import { http } from '../../../lib/api/http';
import { getSessionUser, getSessionUserId, refreshSessionUser } from '../../../services/sessionUser';

const PRICE_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const CREDIT_PACKS = [
  {
    id: 'starter',
    name: 'Paquete Starter',
    description: 'Ideal para impulsar las primeras campañas.',
    ppcCredits: 120,
    leadCredits: 20,
    price: 49,
  },
  {
    id: 'growth',
    name: 'Paquete Growth',
    description: 'Balance entre visibilidad y generación de demanda.',
    ppcCredits: 320,
    leadCredits: 60,
    price: 129,
  },
  {
    id: 'scale',
    name: 'Paquete Scale',
    description: 'Pensado para campañas continuas a lo largo del mes.',
    ppcCredits: 720,
    leadCredits: 150,
    price: 249,
  },
  {
    id: 'enterprise',
    name: 'Paquete Enterprise',
    description: 'Máxima exposición para equipos de ventas activos.',
    ppcCredits: 1500,
    leadCredits: 360,
    price: 480,
  },
];

const PAYMENT_METHODS = [
  {
    id: 'corporate-card',
    label: 'Tarjeta corporativa',
    badge: 'Visa terminación ••4242',
    details: 'Cargo único inmediato con comprobante electrónico.',
  },
  {
    id: 'bank-transfer',
    label: 'Transferencia SPEI',
    badge: 'Acreditación 24-48 h',
    details: 'Te enviaremos una referencia para conciliar tu pago.',
  },
  {
    id: 'credits-balance',
    label: 'Saldo disponible',
    badge: 'Combina crédito restante',
    details: 'Si tienes saldo residual, aplicaremos el complemento automático.',
  },
];

const STATUS = {
  REVIEW: 'review',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const formatPrice = value => PRICE_FORMATTER.format(Number(value) || 0);
const RECEIPT_PRICE_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatReceiptAmount = value => RECEIPT_PRICE_FORMATTER.format(Number(value) || 0);

const buildTopupReference = timestamp => {
  if (!timestamp) return '#TOP-0000-00';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '#TOP-0000-00';
  const year = date.getFullYear();
  const suffix = `${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;
  return `#TOP-${year}-${suffix}`;
};

const findPack = (packs, id) =>
  packs.find(item => item.id === id || item.slug === id || item.name?.toLowerCase() === id?.toLowerCase()) ||
  packs[0];

const getNumeric = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const applyCreditsToCurrentUser = async creditsDelta => {
  const delta = getNumeric(creditsDelta);
  if (!delta) return null;
  const profile = await getSessionUser();
  const userId = profile?.id || (await getSessionUserId());
  if (!userId) return null;
  const currentCredits = getNumeric(profile?.credits);
  const nextCredits = currentCredits + delta;
  try {
    const response = await http.patch(`/users/${encodeURIComponent(userId)}`, {
      credits: nextCredits,
    });
    await refreshSessionUser().catch(() => {});
    return response.data || {};
  } catch (error) {
    console.warn('No se pudo actualizar los créditos del usuario en el backend.', error);
    return null;
  }
};

export default function CreditsCheckout() {
  const location = useLocation();
  const navigate = useNavigate();

  const defaultPackId = location.state?.packId || '';
  const preloadedPack = location.state?.pack || null;

  const packs = useMemo(() => CREDIT_PACKS, []);
  const [selectedPackId, setSelectedPackId] = useState(
    preloadedPack?.id || defaultPackId || packs[0].id
  );
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('corporate-card');

  const [status, setStatus] = useState(STATUS.REVIEW);
  const [processingMessage, setProcessingMessage] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const selectedPack = useMemo(
    () => (preloadedPack ? preloadedPack : findPack(packs, selectedPackId)),
    [packs, preloadedPack, selectedPackId]
  );

  const priceLabel = useMemo(() => {
    if (!selectedPack) return 'USD 0';
    return formatPrice(selectedPack.price);
  }, [selectedPack]);

  const simulatePayment = outcome => {
    if (!selectedPack) return;
    setStatus(STATUS.PROCESSING);
    setProcessingMessage(
      outcome === 'success'
        ? 'Simulando recarga y aplicando créditos a tu cuenta...'
        : 'Simulando error de pago. Restaurando estado...'
    );
    setPaymentError('');

    window.setTimeout(() => {
      if (outcome === 'success') {
        const record = {
          packId: selectedPack.id,
          packName: selectedPack.name,
          paymentMethod: selectedPaymentMethod,
          ppcCredits: selectedPack.ppcCredits,
          leadCredits: selectedPack.leadCredits,
          amount: selectedPack.price,
          timestamp: new Date().toISOString(),
        };
        applyCreditsToCurrentUser(record.ppcCredits);
        try {
          window.localStorage.setItem('flotte:lastCreditsCheckout', JSON.stringify(record));
        } catch (storageError) {
          console.warn('No se pudo registrar la recarga simulada.', storageError);
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('flotte:credits-updated', { detail: record }));
        }
        setReceipt(record);
        setStatus(STATUS.SUCCESS);
      } else {
        setPaymentError(
          'El pago no fue autorizado por el emisor en esta simulación. Verifica los datos e inténtalo nuevamente.'
        );
        setStatus(STATUS.FAILURE);
      }
      setProcessingMessage('');
    }, 1500);
  };

  const resetSimulation = () => {
    setStatus(STATUS.REVIEW);
    setProcessingMessage('');
    setPaymentError('');
    setReceipt(null);
  };

  const handleDownloadReceipt = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.print === 'function') {
        window.print();
      }
    } catch (err) {
      console.warn('No fue posible descargar el recibo simulado.', err);
    }
  };

  const handleBack = () => {
    navigate('/dashboard?section=promocionar');
  };

  const renderResult = () => {
    if (!selectedPack) {
      return (
        <div className="plan-checkout__error">
          <XClose aria-hidden="true" />
          <div>
            <h1>No pudimos cargar el paquete</h1>
            <p>Selecciona de nuevo un paquete de créditos para continuar con la simulación.</p>
          </div>
          <button type="button" onClick={handleBack}>
            Volver
          </button>
        </div>
      );
    }

    if (status === STATUS.SUCCESS) {
      const creditsAdded = receipt?.ppcCredits ?? selectedPack.ppcCredits ?? 0;
      const reference = buildTopupReference(receipt?.timestamp);
      const rows = [
        { label: 'Paquete', value: selectedPack.name },
        { label: 'Créditos agregados', value: `${creditsAdded.toLocaleString('es-MX')} créditos` },
        {
          label: 'Total pagado',
          value: formatReceiptAmount(receipt?.amount ?? selectedPack.price),
        },
        { label: 'Referencia', value: reference },
      ];

      return (
        <section className="plan-checkout__result-shell">
          <div className="plan-checkout__result-card credits-result-card">
            <div className="plan-checkout__status-icon is-success">
              <CheckCircle aria-hidden="true" />
            </div>
            <div className="plan-checkout__status-copy">
              <h1>¡Recarga Exitosa!</h1>
              <p>Tu saldo ha sido actualizado.</p>
            </div>
            <span className="credits-result__badge">
              +{creditsAdded.toLocaleString('es-MX')} créditos
            </span>
            <div className="plan-checkout__receipt credits-result__receipt">
              {rows.map(row => (
                <div key={row.label} className="plan-checkout__receipt-row">
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
            <div className="plan-checkout__result-actions">
              <button type="button" className="plan-checkout__pay-btn" onClick={handleBack}>
                Volver a Promocionar
              </button>
              <button type="button" className="plan-checkout__ghost-btn" onClick={handleDownloadReceipt}>
                Descargar recibo
              </button>
              <button type="button" className="plan-checkout__link-btn" onClick={resetSimulation}>
                Simular otra recarga
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (status === STATUS.FAILURE) {
      const attemptedCredits = selectedPack?.ppcCredits ?? 0;
      const rows = [
        { label: 'Paquete', value: selectedPack?.name || 'Paquete no disponible' },
        { label: 'Créditos solicitados', value: `${attemptedCredits.toLocaleString('es-MX')} créditos` },
        { label: 'Total a pagar', value: formatReceiptAmount(selectedPack?.price || 0) },
        { label: 'Referencia', value: 'Pendiente' },
      ];
      return (
        <section className="plan-checkout__result-shell">
          <div className="plan-checkout__result-card credits-result-card">
            <div className="plan-checkout__status-icon is-failure">
              <XClose aria-hidden="true" />
            </div>
            <div className="plan-checkout__status-copy">
              <h1>No pudimos procesar la recarga</h1>
              <p>{paymentError || 'Vuelve a intentarlo en unos minutos.'}</p>
            </div>
            <div className="plan-checkout__receipt credits-result__receipt">
              {rows.map(row => (
                <div key={row.label} className="plan-checkout__receipt-row">
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
            <div className="plan-checkout__result-actions">
              <button type="button" className="plan-checkout__pay-btn" onClick={resetSimulation}>
                Intentar de nuevo
              </button>
              <button type="button" className="plan-checkout__ghost-btn" onClick={handleBack}>
                Salir sin recargar
              </button>
            </div>
          </div>
        </section>
      );
    }

    return null;
  };

  const renderReview = () => {
    if (!selectedPack) {
      return (
        <div className="plan-checkout__error">
          <XClose aria-hidden="true" />
          <div>
            <h1>Selecciona un paquete</h1>
            <p>El paquete solicitado ya no está disponible. Elige uno de la lista para continuar.</p>
          </div>
          <button type="button" onClick={() => setSelectedPackId(packs[0].id)}>
            Usar paquete inicial
          </button>
        </div>
      );
    }

    return (
      <>
        <section className="plan-checkout__intro">
          <p className="plan-checkout__breadcrumb">Checkout de créditos</p>
          <h1>Recargar saldo promocional</h1>
          <p>
            Selecciona un paquete, define el método de pago y confirma el monto en un flujo tipo
            e-commerce.
          </p>
        </section>

        <section className="plan-checkout__grid">
          <div className="plan-checkout__main">
            <article className="plan-checkout__card credits-pack-shell">
              <div className="credits-pack-shell__head">
                <p className="plan-checkout__subheading">Paquetes disponibles</p>
                <h2>Selecciona tu próximo impulso</h2>
                <p>Los créditos y beneficios clave viven dentro de cada tarjeta.</p>
              </div>
              <div className="credits-pack-grid">
                {packs.map(pack => {
                  const isSelected = selectedPackId === pack.id;
                  const showBestValue = pack.id === 'growth';
                  return (
                    <button
                      key={pack.id}
                      type="button"
                      className={`credits-pack-card${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedPackId(pack.id)}
                    >
                      {showBestValue ? <span className="credits-pack-card__badge">Best Value</span> : null}
                      <div className="credits-pack-card__top">
                        <div>
                          <span className="credits-pack-card__title">{pack.name}</span>
                          <p className="credits-pack-card__description">{pack.description}</p>
                        </div>
                        <span className="credits-pack-card__price">{formatPrice(pack.price)}</span>
                      </div>
                      <ul className="credits-pack-card__features">
                        <li>{pack.ppcCredits.toLocaleString('es-MX')} créditos</li>
                      </ul>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="plan-checkout__card plan-checkout__payments-card">
              <div className="plan-checkout__payments-head">
                <p className="plan-checkout__subheading">Método de pago</p>
                <h2>¿Cómo quieres pagar?</h2>
                <p>La descripción se mantiene ordenada debajo del título.</p>
              </div>
              <div className="plan-checkout__payment-group" role="radiogroup" aria-label="Método de pago">
                {PAYMENT_METHODS.map(option => {
                  const isSelected = selectedPaymentMethod === option.id;
                  return (
                    <label
                      key={option.id}
                      className={`plan-checkout__payment-card${isSelected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        className="plan-checkout__radio"
                        name="credits-payment"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => setSelectedPaymentMethod(option.id)}
                      />
                      <div className="plan-checkout__payment-body">
                        <div className="plan-checkout__payment-top">
                          <span className="plan-checkout__payment-title">{option.label}</span>
                        </div>
                        <p className="plan-checkout__payment-helper">{option.badge}</p>
                        <p className="plan-checkout__payment-details">{option.details}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </article>
          </div>

          <aside className="plan-checkout__summary-card credits-summary-card" aria-label="Resumen del pedido">
            <div className="credits-summary__head">
              <p className="plan-checkout__subheading">Resumen del pedido</p>
              <h3>{selectedPack.name}</h3>
            </div>
            <ul className="credits-summary__breakdown">
              <li>
                <span>Paquete elegido</span>
                <strong>{selectedPack.name}</strong>
              </li>
              <li>
                <span>Créditos a recargar</span>
                <strong>{selectedPack.ppcCredits.toLocaleString('es-MX')}</strong>
              </li>
              <li>
                <span>Método de pago</span>
                <strong>
                  {PAYMENT_METHODS.find(method => method.id === selectedPaymentMethod)?.label || '—'}
                </strong>
              </li>
            </ul>
            <div className="credits-summary__total">
              <span>Total</span>
              <strong>{priceLabel}</strong>
            </div>
            {processingMessage ? (
              <p className="plan-checkout__processing credits-summary__processing">
                <CreditCardRefresh aria-hidden="true" />
                {processingMessage}
              </p>
            ) : null}
            {paymentError ? <p className="plan-checkout__error-text">{paymentError}</p> : null}
            <button
              type="button"
              className="credits-summary__pay-btn"
              onClick={() => simulatePayment('success')}
              disabled={status === STATUS.PROCESSING}
            >
              Pagar ahora
            </button>
            <button
              type="button"
              className="credits-summary__ghost-btn"
              onClick={() => simulatePayment('failure')}
              disabled={status === STATUS.PROCESSING}
            >
              Simular pago rechazado
            </button>
            <button
              type="button"
              className="credits-summary__link-btn"
              onClick={handleBack}
              disabled={status === STATUS.PROCESSING}
            >
              Cancelar y volver
            </button>
          </aside>
        </section>
      </>
    );
  };

  return (
    <main className="plan-checkout">
      {status === STATUS.REVIEW ? renderReview() : renderResult()}
    </main>
  );
}
